import { ChatMetaContent, serializePayload } from '@halo/ui-chat-react';
import { Box, Text } from '@chakra-ui/react';
import { useStoreEnhance } from '@halo/zustand-enhance';
import { LastMessageText } from '@halo/ui-chat-react';
import type { Msg, Session } from '@/store/client';
import {
  getIsSystemMessage,
  getSessionUnreadCount,
  isLuckyBoxMessage,
  isSessionCategory,
  SessionCategoryPrefix,
  useAppStore,
} from '@/store/client';
import { useSettingsStore } from '@/store/settings';
import { useIsMyMsg, useUserInfo } from '@/scripts/hooks';
import { MsgsPreviewItem } from '../messages/msgs-preview-item';

export function ChatMetaContentWithData({
  last_message,
  isGroup,
  session,
  isCollapseChat,
  isRobot,
}: {
  last_message: Msg | undefined;
  isGroup: boolean;
  session: Session;
  isCollapseChat?: boolean;
  isRobot?: boolean;
}) {
  const { t } = useTranslation();
  const { chatLines } = useSettingsStore();

  const { sessionStores } = useAppStore();
  const sessionStore = useStoreEnhance(sessionStores[session.chat_id]);
  const { user: lastMsgUser } = useUserInfo(last_message?.owner_id);
  const { unread_count, muted_unread_count } = useMemo(
    () => getSessionUnreadCount(session),
    [session]
  );

  const draftMsg = useMemo(() => {
    if (sessionStore?.draftMessage?.payload && sessionStore?.activated === false) {
      return (
        <Box>
          <LastMessageText>
            <Text as="span" color="fg.error" pr="1">
              [{t('chatList.draft')}]
            </Text>
            {serializePayload.deserializeContent(sessionStore.draftMessage.payload)}
          </LastMessageText>
          {chatLines === 3 && <Box fontSize="0.857em">&nbsp;</Box>}
        </Box>
      );
    }
  }, [sessionStore?.activated, sessionStore?.draftMessage, chatLines, t]);

  const isMyMsg = useIsMyMsg(last_message);
  const isLuckyBox = useMemo(() => isLuckyBoxMessage(last_message), [last_message]);
  const isSystemMsg = useMemo(() => getIsSystemMessage(last_message), [last_message]);

  const lastMessageChatIsGroup = useMemo(() => {
    return isSessionCategory(session?.last_message_chat_id, SessionCategoryPrefix.Group);
  }, [session?.last_message_chat_id]);

  const children = useMemo(() => {
    if (draftMsg) {
      return draftMsg;
    }

    if (last_message) {
      return <MsgsPreviewItem session={session} message={last_message}></MsgsPreviewItem>;
    }
  }, [draftMsg, session, last_message]);
  return (
    <ChatMetaContent
      isSystemMsg={isSystemMsg}
      isGroup={isGroup}
      unreadCount={unread_count}
      mutedUnreadCount={muted_unread_count}
      lastMessage={last_message}
      hasMention={!!session?.at_message_list?.length}
      isLuckyBox={isLuckyBox}
      chatLines={chatLines}
      isMyMessage={isMyMsg}
      lastMsgUser={lastMsgUser}
      draftMsg={draftMsg}
      operateAccess={session.operateAccess}
      isCollapseChat={isCollapseChat}
      groupName={session?.last_message_groupname}
      lastMessageChatIsGroup={lastMessageChatIsGroup}
      isRobot={isRobot}
    >
      {children}
    </ChatMetaContent>
  );
}
